// Helper utilitario para cálculo determinista de diagnósticos y renderizado de plantillas
// Satisface las 4 categorías metodológicas de Equipo Alerce

export const DIAGNOSTIC_CATEGORIES = {
  RECUPERAR_TIEMPO: {
    id: 'recuperar_tiempo',
    label: 'Recuperar tiempo',
    badgeColor: 'amber',
    summaryDefault: 'Tu cuello de botella principal es el tiempo absorbido por tareas manuales y repetitivas, limitando tu capacidad de foco y ventas.',
    actionDefault: 'Mapear y automatizar los flujos manuales de mayor carga horaria semanal (como traspaso de datos y confirmaciones).'
  },
  ORDENAR_INFORMACION: {
    id: 'ordenar_informacion',
    label: 'Ordenar información',
    badgeColor: 'blue',
    summaryDefault: 'La información operativa está dispersa entre múltiples canales o planillas, generando retrabajo y fricción al operar.',
    actionDefault: 'Unificar la gestión en una única base de datos centralizada y estandarizar las fuentes de entrada.'
  },
  MEJORAR_CONTROL: {
    id: 'mejorar_control',
    label: 'Mejorar control',
    badgeColor: 'emerald',
    summaryDefault: 'Falta visibilidad en tiempo real sobre los estados de tareas, entregas y cobros, impidiendo anticipar problemas operativos.',
    actionDefault: 'Implementar un pipeline con estados unificados, alertas automáticas de seguimiento y métricas clave en un panel.'
  },
  PREPARAR_PROXIMA_ETAPA: {
    id: 'preparar_proxima_etapa',
    label: 'Preparar la próxima etapa',
    badgeColor: 'purple',
    summaryDefault: 'El negocio cuenta con tracción pero necesita procesos estandarizados y sistemas sólidos para poder delegar y crecer sin desbordes.',
    actionDefault: 'Documentar los procedimientos operativos clave y crear sistemas modulares que funcionen sin depender del fundador.'
  }
};

// Motor determinista para calcular o validar el diagnóstico a partir de las respuestas
export function calculateDiagnosticResult(answers = {}) {
  const q1 = (answers.q1_problem || '').toLowerCase();
  const q2 = (answers.q2_current_method || '').toLowerCase();
  const q3 = (answers.q3_impact || '').toLowerCase();
  const q4 = (answers.q4_priority || '').toLowerCase();

  const combinedText = `${q4} ${q1} ${q3} ${q2}`;

  // 1. Coincidencia por prioridad (q4_priority) o palabras clave
  if (
    combinedText.includes('tiempo') ||
    combinedText.includes('automatiz') ||
    combinedText.includes('manual') ||
    combinedText.includes('horas') ||
    combinedText.includes('repetitiv') ||
    combinedText.includes('delegar')
  ) {
    return {
      diagnosis_category: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.label,
      diagnosis_category_id: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.id,
      diagnosis_summary: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.summaryDefault,
      recommended_action: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.actionDefault
    };
  }

  if (
    combinedText.includes('orden') ||
    combinedText.includes('informacion') ||
    combinedText.includes('planill') ||
    combinedText.includes('excel') ||
    combinedText.includes('dispers') ||
    combinedText.includes('desorganiz')
  ) {
    return {
      diagnosis_category: DIAGNOSTIC_CATEGORIES.ORDENAR_INFORMACION.label,
      diagnosis_category_id: DIAGNOSTIC_CATEGORIES.ORDENAR_INFORMACION.id,
      diagnosis_summary: DIAGNOSTIC_CATEGORIES.ORDENAR_INFORMACION.summaryDefault,
      recommended_action: DIAGNOSTIC_CATEGORIES.ORDENAR_INFORMACION.actionDefault
    };
  }

  if (
    combinedText.includes('control') ||
    combinedText.includes('seguim') ||
    combinedText.includes('metric') ||
    combinedText.includes('visibil') ||
    combinedText.includes('trazabil') ||
    combinedText.includes('estado') ||
    combinedText.includes('perdid')
  ) {
    return {
      diagnosis_category: DIAGNOSTIC_CATEGORIES.MEJORAR_CONTROL.label,
      diagnosis_category_id: DIAGNOSTIC_CATEGORIES.MEJORAR_CONTROL.id,
      diagnosis_summary: DIAGNOSTIC_CATEGORIES.MEJORAR_CONTROL.summaryDefault,
      recommended_action: DIAGNOSTIC_CATEGORIES.MEJORAR_CONTROL.actionDefault
    };
  }

  if (
    combinedText.includes('etapa') ||
    combinedText.includes('crec') ||
    combinedText.includes('escal') ||
    combinedText.includes('equip') ||
    combinedText.includes('proxim') ||
    combinedText.includes('expand')
  ) {
    return {
      diagnosis_category: DIAGNOSTIC_CATEGORIES.PREPARAR_PROXIMA_ETAPA.label,
      diagnosis_category_id: DIAGNOSTIC_CATEGORIES.PREPARAR_PROXIMA_ETAPA.id,
      diagnosis_summary: DIAGNOSTIC_CATEGORIES.PREPARAR_PROXIMA_ETAPA.summaryDefault,
      recommended_action: DIAGNOSTIC_CATEGORIES.PREPARAR_PROXIMA_ETAPA.actionDefault
    };
  }

  // Fallback por defecto si no hay coincidencia explícita
  return {
    diagnosis_category: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.label,
    diagnosis_category_id: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.id,
    diagnosis_summary: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.summaryDefault,
    recommended_action: DIAGNOSTIC_CATEGORIES.RECUPERAR_TIEMPO.actionDefault
  };
}

// Normalizador de payload (soporta formato agrupado o formato plano)
export function normalizeDiagnosticPayload(body = {}) {
  const contact = body.contact || {};
  const answers = body.answers || {};
  const diagnosis = body.diagnosis || {};
  const tracking = body.tracking || {};

  const name = (contact.name || body.name || '').trim();
  const email = (contact.email || body.email || '').toLowerCase().trim();
  const business_name = (contact.business_name || body.business_name || body.company || '').trim();

  const q1_problem = (answers.q1_problem || body.q1_problem || '').trim();
  const q2_current_method = (answers.q2_current_method || body.q2_current_method || '').trim();
  const q3_impact = (answers.q3_impact || body.q3_impact || '').trim();
  const q4_priority = (answers.q4_priority || body.q4_priority || '').trim();

  let diagnosis_category = (diagnosis.diagnosis_category || body.diagnosis_category || '').trim();
  let diagnosis_summary = (diagnosis.diagnosis_summary || body.diagnosis_summary || '').trim();
  let recommended_action = (diagnosis.recommended_action || body.recommended_action || '').trim();

  // Si falta alguno de los 3 campos de diagnóstico, calcular determinísticamente
  if (!diagnosis_category || !diagnosis_summary || !recommended_action) {
    const calc = calculateDiagnosticResult({ q1_problem, q2_current_method, q3_impact, q4_priority });
    if (!diagnosis_category) diagnosis_category = calc.diagnosis_category;
    if (!diagnosis_summary) diagnosis_summary = calc.diagnosis_summary;
    if (!recommended_action) recommended_action = calc.recommended_action;
  }

  const source = (tracking.source || body.source || 'equipo_alerce_web').trim();
  const form_name = (tracking.form_name || body.form_name || 'diagnostico_inicial').trim();
  const submitted_at = tracking.submitted_at || body.submitted_at || new Date().toISOString();
  const consent_marketing = Boolean(tracking.consent_marketing !== undefined ? tracking.consent_marketing : body.consent_marketing);

  return {
    contact: { name, email, business_name },
    answers: { q1_problem, q2_current_method, q3_impact, q4_priority },
    diagnosis: { diagnosis_category, diagnosis_summary, recommended_action },
    tracking: { source, form_name, submitted_at, consent_marketing }
  };
}

// Interpolador de variables en plantillas de email
export function renderEmailTemplate(templateContent, variables = {}) {
  if (!templateContent) return '';
  let rendered = templateContent;

  const vars = {
    name: variables.name || 'Hola',
    business_name: variables.business_name || variables.company || '',
    company: variables.business_name || variables.company || '',
    email: variables.email || '',
    diagnosis_category: variables.diagnosis_category || '',
    diagnosis_summary: variables.diagnosis_summary || '',
    recommended_action: variables.recommended_action || '',
    q1_problem: variables.q1_problem || '',
    q2_current_method: variables.q2_current_method || '',
    q3_impact: variables.q3_impact || '',
    q4_priority: variables.q4_priority || '',
    source: variables.source || 'Web Equipo Alerce',
    date: new Date().toLocaleDateString('es-AR')
  };

  Object.entries(vars).forEach(([key, val]) => {
    const pattern = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
    rendered = rendered.replace(pattern, String(val));
  });

  return rendered;
}

// Generador de botón HTML para emails (compatible con Outlook, Gmail, Webmail y móvil)
export function renderCtaButtonHtml({ text, url, style = 'acid' }) {
  const btnText = text || 'Quiero mi diagnóstico';
  const btnUrl = url || '#';

  let bg = '#D2F26B';
  let color = '#0D1A14';
  let border = '1px solid #B9DC45';
  let arrowBg = '#0D1A14';
  let arrowColor = '#D2F26B';

  if (style === 'forest') {
    bg = '#0F3B2C';
    color = '#F3F0E8';
    border = '1px solid #0F3B2C';
    arrowBg = '#D2F26B';
    arrowColor = '#0D1A14';
  } else if (style === 'ink') {
    bg = '#0D1A14';
    color = '#F3F0E8';
    border = '1px solid #0D1A14';
    arrowBg = '#D2F26B';
    arrowColor = '#0D1A14';
  } else if (style === 'outline') {
    bg = 'transparent';
    color = '#0D1A14';
    border = '1px solid rgba(13,26,20,0.3)';
    arrowBg = '#0D1A14';
    arrowColor = '#F3F0E8';
  }

  return `
    <table border="0" cellpadding="0" cellspacing="0" style="margin: 16px 0; display: inline-table;" data-cta-block="true">
      <tr>
        <td align="center" style="border-radius: 9999px; background-color: ${bg}; border: ${border};">
          <a href="${btnUrl}" target="_blank" style="display: inline-block; padding: 10px 22px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 13px; font-weight: 700; color: ${color}; text-decoration: none; border-radius: 9999px;">
            <span>${btnText}</span>
            <span style="display: inline-block; margin-left: 8px; width: 16px; height: 16px; line-height: 16px; border-radius: 9999px; background-color: ${arrowBg}; color: ${arrowColor}; text-align: center; font-size: 10px;">➔</span>
          </a>
        </td>
      </tr>
    </table>
  `.trim();
}

// Inyector universal de CTAs en contenido HTML/Markdown de correo
export function injectCtasIntoEmailBody(content = '', ctas = []) {
  if (!content) return '';
  let rendered = content;
  const firstCta = Array.isArray(ctas) && ctas.length > 0 ? ctas[0] : null;

  if (!firstCta || !firstCta.url) {
    // Si no hay CTA configurado pero quedó el tag {{cta}}, limpiarlo
    rendered = rendered.replace(/\{\{\s*cta(?::[a-z]+)?\s*\}\}/gi, '');
    return rendered;
  }

  const defaultStyle = firstCta.style || 'acid';
  let replaced = false;

  // 1. Reemplazar tags con estilo específico ej: {{cta:forest}}, {{cta:acid}}, {{cta:ink}}, {{cta:outline}}
  ['acid', 'forest', 'ink', 'outline'].forEach(st => {
    const pattern = new RegExp(`\\{\\{\\s*cta:${st}\\s*\\}\\}`, 'gi');
    if (pattern.test(rendered)) {
      rendered = rendered.replace(pattern, renderCtaButtonHtml({ text: firstCta.text, url: firstCta.url, style: st }));
      replaced = true;
    }
  });

  // 2. Reemplazar tag genérico {{cta}}
  if (rendered.includes('{{cta}}') || /\{\{\s*cta\s*\}\}/i.test(rendered)) {
    rendered = rendered.replace(/\{\{\s*cta\s*\}\}/gi, renderCtaButtonHtml({ text: firstCta.text, url: firstCta.url, style: defaultStyle }));
    replaced = true;
  }

  // 3. Reemplazar corchetes e.g. [Texto del Botón]
  if (firstCta.text) {
    const escapedText = firstCta.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const bracketPattern = new RegExp(`\\[${escapedText}\\]`, 'gi');
    if (bracketPattern.test(rendered)) {
      rendered = rendered.replace(bracketPattern, renderCtaButtonHtml({ text: firstCta.text, url: firstCta.url, style: defaultStyle }));
      replaced = true;
    }
  }

  // 4. Si no se ubicó explícitamente y no es un documento HTML completo, concatenar al pie
  if (!replaced) {
    const isFullHtmlDoc = /<!DOCTYPE|<html|<body/i.test(rendered);
    if (!isFullHtmlDoc) {
      rendered += '\n\n' + renderCtaButtonHtml({ text: firstCta.text, url: firstCta.url, style: defaultStyle });
    }
  }

  return rendered;
}
